import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Upload, FileJson, BookOpen, 
  DollarSign, Loader2, CheckCircle, AlertCircle, Eye, 
  Clock, HelpCircle, ChevronDown, ChevronUp, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCreateCourse = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Manual creation state
  const [courseData, setCourseData] = useState({
    title: '', description: '', price: 29.99, lesson_price: 7,
    category: 'general', level: 'beginner', thumbnail_url: '', is_free: false
  });
  
  // JSON import state
  const [jsonInput, setJsonInput] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState({});
  const fileInputRef = React.useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) { toast.error('Please upload a .json file'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      let text = ev.target.result
        .replace(/^\uFEFF/, '').replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"')
        .replace(/\u2013/g, '-').replace(/\u2014/g, '-').trim();
      setJsonInput(text);
      setPreviewData(null);
      setImportResult(null);
      toast.success(`Loaded ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  React.useEffect(() => {
    if (user?.role !== 'admin') { toast.error('Admin access required'); navigate('/'); }
  }, [user, navigate]);

  const cleanAndParse = () => {
    let cleanedInput = jsonInput
      .replace(/^\uFEFF/, '').replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"')
      .replace(/\u2013/g, '-').replace(/\u2014/g, '-').replace(/\t/g, '  ').trim();
    
    let parsed;
    try { parsed = JSON.parse(cleanedInput); } catch (e) {
      toast.error(`Invalid JSON syntax: ${e.message}`); return null;
    }
    
    let courses;
    if (Array.isArray(parsed)) { courses = parsed; }
    else if (parsed.courses && Array.isArray(parsed.courses)) { courses = parsed.courses; }
    else if (parsed.title || parsed.name || parsed.courseTitle) { courses = [parsed]; }
    else if (parsed.course) { courses = [parsed]; }
    else if (parsed.modules || parsed.lessons) { courses = [parsed]; }
    else { toast.error('Invalid format.'); return null; }
    return courses;
  };

  const handlePreview = async () => {
    const courses = cleanAndParse();
    if (!courses) return;
    setPreviewLoading(true);
    setPreviewData(null);
    setImportResult(null);
    try {
      const response = await axios.post(`${API}/admin/courses/import/preview`,
        { courses }, { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewData(response.data.preview);
      setExpandedCourses({});
      toast.success('Preview ready. Review and confirm import.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed.');
    } finally { setPreviewLoading(false); }
  };

  const handleConfirmImport = async () => {
    const courses = cleanAndParse();
    if (!courses) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/courses/import`,
        { courses }, { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportResult(response.data);
      setPreviewData(null);
      if (response.data.errors.length === 0) {
        toast.success(`Imported ${response.data.created.length + response.data.updated.length} course(s)!`);
        setJsonInput('');
      } else { toast.warning('Import completed with errors.'); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed.');
    } finally { setLoading(false); }
  };

  const handleManualCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/courses/create`, courseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course created!');
      navigate(`/admin/courses/${response.data.course_id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally { setLoading(false); }
  };

  const toggleCourseExpand = (idx) => {
    setExpandedCourses(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-create-course">
      <Navbar />
      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" className="mb-6 -ml-4" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">Create Platform Course</h1>
              <p className="text-slate-600">Add a new course to AISkillFlow</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <Plus className="w-4 h-4" /> Manual Creation
              </TabsTrigger>
              <TabsTrigger value="import" className="flex items-center gap-2">
                <FileJson className="w-4 h-4" /> JSON Import
              </TabsTrigger>
            </TabsList>

            {/* Manual Creation Tab */}
            <TabsContent value="manual">
              <Card>
                <CardHeader>
                  <CardTitle>Course Details</CardTitle>
                  <CardDescription>Fill in the basic information. You can add lessons after creation.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleManualCreate} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label htmlFor="title">Course Title *</Label>
                        <Input id="title" value={courseData.title} onChange={(e) => setCourseData({...courseData, title: e.target.value})} placeholder="e.g., Mastering Email Marketing" required data-testid="course-title-input" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="description">Description *</Label>
                        <Textarea id="description" value={courseData.description} onChange={(e) => setCourseData({...courseData, description: e.target.value})} placeholder="What will students learn?" rows={4} required data-testid="course-description-input" />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={courseData.category} onValueChange={(v) => setCourseData({...courseData, category: v})}>
                          <SelectTrigger data-testid="course-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="ai-tools">AI Tools</SelectItem>
                            <SelectItem value="marketing">Marketing</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="productivity">Productivity</SelectItem>
                            <SelectItem value="development">Development</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Level</Label>
                        <Select value={courseData.level} onValueChange={(v) => setCourseData({...courseData, level: v})}>
                          <SelectTrigger data-testid="course-level-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Bundle Price ($)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input type="number" step="0.01" min="0" value={courseData.price} onChange={(e) => setCourseData({...courseData, price: parseFloat(e.target.value)})} className="pl-9" data-testid="course-price-input" />
                        </div>
                      </div>
                      <div>
                        <Label>Per Lesson Price ($)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input type="number" step="0.01" min="0" value={courseData.lesson_price} onChange={(e) => setCourseData({...courseData, lesson_price: parseFloat(e.target.value)})} className="pl-9" data-testid="course-lesson-price-input" />
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Thumbnail URL</Label>
                        <Input value={courseData.thumbnail_url} onChange={(e) => setCourseData({...courseData, thumbnail_url: e.target.value})} placeholder="https://example.com/image.jpg" data-testid="course-thumbnail-input" />
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch id="is_free" checked={courseData.is_free} onCheckedChange={(c) => setCourseData({...courseData, is_free: c})} data-testid="course-free-toggle" />
                        <Label htmlFor="is_free">Make this course free</Label>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="create-course-btn">
                      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Course</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* JSON Import Tab */}
            <TabsContent value="import">
              <Card>
                <CardHeader>
                  <CardTitle>Import from JSON</CardTitle>
                  <CardDescription>Paste JSON to import courses. Preview before importing.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="json">Course JSON</Label>
                      <div className="flex items-center gap-2">
                        <input type="file" ref={fileInputRef} accept=".json" onChange={handleFileUpload} className="hidden" data-testid="json-file-input" />
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-json-file-btn">
                          <Upload className="w-4 h-4 mr-1" /> Upload .json File
                        </Button>
                        {jsonInput && <span className="text-xs text-slate-500">{(jsonInput.length / 1024).toFixed(1)} KB loaded</span>}
                      </div>
                    </div>
                    <Textarea id="json" value={jsonInput} onChange={(e) => { setJsonInput(e.target.value); setPreviewData(null); setImportResult(null); }} placeholder="Paste your JSON here or upload a .json file..." rows={12} className="font-mono text-sm" data-testid="json-import-input" />
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={handlePreview} disabled={previewLoading || !jsonInput.trim()} variant="outline" className="flex-1" data-testid="preview-import-btn">
                      {previewLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing...</> : <><Eye className="w-4 h-4 mr-2" /> Preview Import</>}
                    </Button>
                    {previewData && !previewData.some(c => c.error) && (
                      <Button onClick={handleConfirmImport} disabled={loading} className="flex-1" data-testid="confirm-import-btn">
                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Confirm Import</>}
                      </Button>
                    )}
                  </div>

                  {/* Preview Results */}
                  {previewData && (
                    <div className="space-y-4" data-testid="import-preview-results">
                      <h4 className="font-semibold text-slate-900 text-lg">Import Preview</h4>
                      {previewData.map((course, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`preview-course-${idx}`}>
                          {course.error ? (
                            <div className="p-4 bg-red-50 flex items-start gap-2 text-red-700">
                              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                              <div>
                                <p className="font-medium">{course.title}</p>
                                <p className="text-sm">{course.error}</p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="p-4 bg-white cursor-pointer" onClick={() => toggleCourseExpand(idx)}>
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h5 className="font-semibold text-slate-900">{course.title}</h5>
                                      {course.will_update ? (
                                        <Badge variant="secondary" className="text-xs">Will Update</Badge>
                                      ) : (
                                        <Badge className="text-xs bg-green-100 text-green-800">New Course</Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                      <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {course.total_lessons} lessons</span>
                                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {course.total_duration_minutes} min</span>
                                      <span className="flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> {course.total_quiz_questions} quiz Q's</span>
                                      <span className="capitalize">{course.level}</span>
                                      {!course.is_free && <span>${course.price}</span>}
                                    </div>
                                  </div>
                                  {expandedCourses[idx] ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                                </div>
                              </div>
                              {expandedCourses[idx] && (
                                <div className="border-t border-slate-100 bg-slate-50 p-4">
                                  <div className="space-y-1">
                                    {course.lessons.map((lesson, li) => (
                                      <div key={li} className="flex items-center gap-3 py-1.5 text-sm">
                                        <span className="text-slate-400 w-6 text-right">{li + 1}.</span>
                                        <span className="flex-1 text-slate-700">{lesson.title}</span>
                                        {lesson.module_title && <Badge variant="outline" className="text-xs">{lesson.module_title}</Badge>}
                                        <span className="text-xs text-slate-400">{lesson.duration_minutes}m</span>
                                        <Badge variant={lesson.has_content ? "default" : "destructive"} className="text-xs">
                                          {lesson.has_content ? "Content" : "No content"}
                                        </Badge>
                                        {lesson.quiz_questions > 0 && (
                                          <Badge className="text-xs bg-purple-100 text-purple-800">{lesson.quiz_questions} Q</Badge>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Import Results */}
                  {importResult && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-lg" data-testid="import-results">
                      <h4 className="font-medium">Import Results</h4>
                      {importResult.created.length > 0 && (
                        <div className="flex items-start gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5 mt-0.5" />
                          <div>
                            <p className="font-medium">Created ({importResult.created.length})</p>
                            <ul className="text-sm">{importResult.created.map((c, i) => <li key={i}>- {c.title}</li>)}</ul>
                          </div>
                        </div>
                      )}
                      {importResult.updated.length > 0 && (
                        <div className="flex items-start gap-2 text-blue-600">
                          <CheckCircle className="w-5 h-5 mt-0.5" />
                          <div>
                            <p className="font-medium">Updated ({importResult.updated.length})</p>
                            <ul className="text-sm">{importResult.updated.map((c, i) => <li key={i}>- {c.title}</li>)}</ul>
                          </div>
                        </div>
                      )}
                      {importResult.errors.length > 0 && (
                        <div className="flex items-start gap-2 text-red-600">
                          <AlertCircle className="w-5 h-5 mt-0.5" />
                          <div>
                            <p className="font-medium">Errors ({importResult.errors.length})</p>
                            <ul className="text-sm">{importResult.errors.map((e, i) => <li key={i}>- {e.course}: {e.error}</li>)}</ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-4 bg-blue-50 rounded-lg text-sm">
                    <h4 className="font-medium text-blue-900 mb-2">Supported JSON Formats</h4>
                    <ul className="text-blue-800 space-y-1">
                      <li>- Single course object, array of courses, or <code className="bg-blue-100 px-1 rounded">{"{ courses: [...] }"}</code></li>
                      <li>- Module-based: <code className="bg-blue-100 px-1 rounded">modules[].lessons[]</code> with quizzes at module or lesson level</li>
                      <li>- Flexible keys: <code className="bg-blue-100 px-1 rounded">title/courseTitle/name</code>, <code className="bg-blue-100 px-1 rounded">content/lessonBody/content_md</code></li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateCourse;
